import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Building2, DollarSign,
  Users, Landmark, ArrowRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import { dashboardApi } from '../../api/client';
import { formatCurrency, chartColors, modules } from '../../theme/constants';
import { useAuth } from '../../context/AuthContext';

interface EntityBreakdown {
  entityId: string;
  entityName: string;
  effectiveOwnershipPct: number;
  directAssets: number;
  directLiabilities: number;
  directNAV: number;
  memberShare: number;
}

interface DashboardData {
  office: any;
  netWorth: {
    consolidatedNetWorth: number;
    memberName: string | null;
    grossAssets: number;
    grossLiabilities: number;
    simpleNetWorth: number;
    totalAssets: number;
    totalLiabilities: number;
    netWorth: number;
    liquidAssets: number;
    illiquidAssets: number;
    ltvRatio: number;
    entityBreakdown: EntityBreakdown[];
  };
  assetAllocation: Array<{ type: string; count: number; value: number }>;
  cashFlow: {
    annualInflows: number;
    annualOutflows: number;
    netCashFlow: number;
  };
  familyMembers: { byGeneration: Array<{ generation: number; count: number }> };
  entities: { byType: Array<{ type: string; count: number }> };
  activeScenarios: number;
}

function StatCard({
  label,
  value,
  subtitle,
  icon: Icon,
  accent = 'navy',
}: {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ComponentType<any>;
  accent?: 'navy' | 'hunter' | 'burgundy' | 'gold';
}) {
  const accentColors = {
    navy: 'bg-navy-50 text-navy-600',
    hunter: 'bg-hunter-50 text-hunter-600',
    burgundy: 'bg-burgundy-50 text-burgundy-600',
    gold: 'bg-gold-50 text-gold-600',
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="stat-label">{label}</p>
          <p className="stat-value mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-navy-400 font-sans mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-2.5 rounded-lg ${accentColors[accent]}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const response = await dashboardApi.summary();
        setData(response.data.data);
      } catch (err) {
        console.error('Failed to load dashboard', err);
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-navy-400 font-sans text-sm">
          Loading dashboard...
        </div>
      </div>
    );
  }

  // Empty state — no data yet
  if (!data || data.netWorth.totalAssets === 0) {
    return (
      <div className="max-w-2xl mx-auto mt-12 text-center">
        <h2 className="font-display text-display-md text-navy-800 mb-3">
          Welcome to Osprey Legacy
        </h2>
        <p className="text-navy-500 font-sans mb-8 leading-relaxed">
          Start by adding your family members, assets, and liabilities. Once your data is in,
          every module will come alive with personalized analysis.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/members"
            className="card hover:shadow-elegant-md transition-shadow text-left"
          >
            <Users size={24} className="text-navy-500 mb-3" />
            <p className="font-sans font-semibold text-navy-800 text-sm">
              Add Family Members
            </p>
            <p className="text-xs text-navy-400 mt-1">
              Patriarch, spouse, children, grandchildren
            </p>
          </Link>

          <Link
            to="/assets"
            className="card hover:shadow-elegant-md transition-shadow text-left"
          >
            <DollarSign size={24} className="text-hunter-500 mb-3" />
            <p className="font-sans font-semibold text-navy-800 text-sm">
              Add Assets
            </p>
            <p className="text-xs text-navy-400 mt-1">
              Real estate, equities, private investments
            </p>
          </Link>

          <Link
            to="/liabilities-mgmt"
            className="card hover:shadow-elegant-md transition-shadow text-left"
          >
            <Building2 size={24} className="text-burgundy-500 mb-3" />
            <p className="font-sans font-semibold text-navy-800 text-sm">
              Add Liabilities
            </p>
            <p className="text-xs text-navy-400 mt-1">
              Mortgages, credit lines, margin loans
            </p>
          </Link>
        </div>
      </div>
    );
  }

  const nw = data.netWorth;
  const breakdown = nw.entityBreakdown || [];
  const hasConsolidation = breakdown.length > 0;

  return (
    <div className="space-y-6">
      {/* Office header */}
      <div>
        <p className="text-sm text-navy-400 font-sans">
          {data.office?.name || 'Family Office'}
          {nw.memberName && (
            <span className="ml-2 text-navy-300">
              — Consolidated for {nw.memberName}
            </span>
          )}
        </p>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Consolidated Net Worth"
          value={formatCurrency(nw.consolidatedNetWorth ?? nw.netWorth, true)}
          subtitle={hasConsolidation && nw.simpleNetWorth !== nw.consolidatedNetWorth
            ? `Gross: ${formatCurrency(nw.simpleNetWorth, true)}`
            : undefined
          }
          icon={DollarSign}
          accent="navy"
        />
        <StatCard
          label="Liquid Assets"
          value={formatCurrency(nw.liquidAssets, true)}
          icon={TrendingUp}
          accent="hunter"
        />
        <StatCard
          label="Total Liabilities"
          value={formatCurrency(nw.grossLiabilities ?? nw.totalLiabilities, true)}
          icon={TrendingDown}
          accent="burgundy"
        />
        <StatCard
          label="LTV Ratio"
          value={`${(nw.ltvRatio * 100).toFixed(1)}%`}
          icon={Landmark}
          accent="gold"
        />
      </div>

      {/* Entity Breakdown (collapsible) */}
      {hasConsolidation && (
        <div className="card">
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="w-full flex items-center justify-between"
          >
            <h3 className="card-header mb-0">Ownership-Adjusted Entity Values</h3>
            {showBreakdown ? (
              <ChevronUp size={18} className="text-navy-400" />
            ) : (
              <ChevronDown size={18} className="text-navy-400" />
            )}
          </button>

          {showBreakdown && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm font-sans">
                <thead>
                  <tr className="border-b border-ivory-300">
                    <th className="text-left py-2 text-navy-500 font-medium text-xs uppercase tracking-wide">Entity</th>
                    <th className="text-right py-2 text-navy-500 font-medium text-xs uppercase tracking-wide">Ownership</th>
                    <th className="text-right py-2 text-navy-500 font-medium text-xs uppercase tracking-wide">Direct Assets</th>
                    <th className="text-right py-2 text-navy-500 font-medium text-xs uppercase tracking-wide">Liabilities</th>
                    <th className="text-right py-2 text-navy-500 font-medium text-xs uppercase tracking-wide">Entity NAV</th>
                    <th className="text-right py-2 text-navy-500 font-medium text-xs uppercase tracking-wide">{nw.memberName?.split(' ')[0] || 'Member'}'s Share</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((entity) => (
                    <tr key={entity.entityId} className="border-b border-ivory-200 hover:bg-ivory-50">
                      <td className="py-2 text-navy-800 font-medium">{entity.entityName}</td>
                      <td className="py-2 text-right text-navy-600 tabular-nums">
                        {entity.effectiveOwnershipPct.toFixed(2)}%
                      </td>
                      <td className="py-2 text-right text-navy-600 tabular-nums">
                        {formatCurrency(entity.directAssets)}
                      </td>
                      <td className="py-2 text-right text-burgundy-600 tabular-nums">
                        {entity.directLiabilities > 0
                          ? `(${formatCurrency(entity.directLiabilities)})`
                          : '—'}
                      </td>
                      <td className="py-2 text-right text-navy-700 tabular-nums font-medium">
                        {formatCurrency(entity.directNAV)}
                      </td>
                      <td className={`py-2 text-right tabular-nums font-semibold ${
                        entity.memberShare >= 0 ? 'text-navy-800' : 'text-burgundy-700'
                      }`}>
                        {formatCurrency(entity.memberShare)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-navy-200">
                    <td className="py-3 text-navy-800 font-semibold" colSpan={5}>
                      Consolidated Total
                    </td>
                    <td className="py-3 text-right text-navy-900 font-bold tabular-nums text-base">
                      {formatCurrency(nw.consolidatedNetWorth ?? nw.netWorth)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Asset Allocation Pie */}
        <div className="card">
          <h3 className="card-header">Asset Allocation</h3>
          {data.assetAllocation.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={data.assetAllocation}
                  dataKey="value"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  paddingAngle={2}
                  stroke="none"
                >
                  {data.assetAllocation.map((_, i) => (
                    <Cell key={i} fill={chartColors[i % chartColors.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => formatCurrency(value, true)}
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #EDE7D9',
                    boxShadow: '0 4px 6px rgba(15,29,51,0.07)',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '13px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-navy-400 py-12 text-center">
              Add assets to see allocation
            </p>
          )}
        </div>

        {/* Cash Flow Bar Chart */}
        <div className="card">
          <h3 className="card-header">Annual Cash Flow</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={[
                { name: 'Inflows', value: data.cashFlow.annualInflows },
                { name: 'Outflows', value: data.cashFlow.annualOutflows },
                { name: 'Net', value: data.cashFlow.netCashFlow },
              ]}
              margin={{ top: 10, right: 10, bottom: 0, left: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#EDE7D9" />
              <XAxis
                dataKey="name"
                tick={{ fontFamily: 'Lato', fontSize: 12, fill: '#566F96' }}
              />
              <YAxis
                tickFormatter={(v) => formatCurrency(v, true)}
                tick={{ fontFamily: 'Lato', fontSize: 11, fill: '#7489AA' }}
              />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #EDE7D9',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '13px',
                }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                <Cell fill="#2D6B3F" />
                <Cell fill="#8B2331" />
                <Cell fill="#253D62" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Module quick-access */}
      <div className="card">
        <h3 className="card-header">Analysis Modules</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {modules.filter((m) => m.key !== 'dashboard').map((mod) => (
            <Link
              key={mod.key}
              to={mod.path}
              className="flex items-center justify-between p-3 rounded-md border border-ivory-300 hover:border-navy-200 hover:bg-ivory-50 transition-colors group"
            >
              <span className="font-sans text-sm text-navy-700 group-hover:text-navy-900">
                {mod.label}
              </span>
              <ArrowRight
                size={14}
                className="text-navy-300 group-hover:text-navy-500 transition-colors"
              />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
